import { memo } from 'react';
import PropTypes from 'prop-types';

import Tooltip from '../../../Tooltip.jsx';

/**
 * A mark that says what it encodes.
 *
 * A heat map cell is a coloured square and nothing else: the reader can see
 * that Wednesday at 1pm is darker without ever learning what darker *means*.
 * Same for a slice, a bar, a funnel stage. Every mark that carries a value
 * wears one of these, so the value and its label are one hover away — through
 * the shared `Tooltip`, so it matches every other tooltip in the product.
 *
 * `fill` is the trap worth knowing about. `Tooltip` puts its own `div` between
 * this component's parent and the mark, so the mark stops being the grid or
 * flex item it was sized as. A bar that must fill its column needs
 * `fill="flex h-full w-full"`; a row inside a list needs `fill="block w-full"`.
 * Left unset, the wrapper shrink-wraps, which is right for a dot and wrong for
 * anything that was stretching.
 *
 * The delay is short — 80ms rather than the platform default 300 — because a
 * reader sweeping a chart is asking about mark after mark, and a third of a
 * second each time reads as the chart being slow to answer.
 *
 * @param {object} props
 * @param {string} props.label What the mark encodes: value, category, comparison.
 * @param {string} [props.fill] Classes for the hover wrapper. See above.
 * @param {import('react').ReactNode} props.children The mark itself.
 */
function ChartDataPoint({ label, fill, children }) {
  return (
    <Tooltip
      content={label}
      size="compact"
      delay={80}
      closeDelay={80}
      triggerClassName={fill ?? 'inline-flex w-fit cursor-default'}
    >
      {children}
    </Tooltip>
  );
}

ChartDataPoint.propTypes = {
  label: PropTypes.string.isRequired,
  fill: PropTypes.string,
  children: PropTypes.node,
};

export default memo(ChartDataPoint);
